'use client'

import { useLocale, useTranslations } from 'next-intl'
import { type ReactNode, useCallback } from 'react'

import {
  LeaderboardBoard,
  LeaderboardLoading
} from '@/features/leaderboard/components/LeaderboardBoard'
import { LeaderboardLoadError } from '@/features/leaderboard/components/LeaderboardLoadError'
import { FanAvatar } from '@/features/leaderboard/components/RowParts'
import { formatLeaderboardNumber } from '@/features/leaderboard/formatLeaderboardNumber'
import { usePagedLeaderboard } from '@/features/leaderboard/usePagedLeaderboard'
import { useClubMe } from '@/hooks/useClubMe'
import { useUserMe } from '@/hooks/useUserMe'

import { fetchFansLeaderboardPage } from '../api/fansLeaderboardApi'
import { useFansLeaderboardPosition } from '../hooks/useFansLeaderboardPosition'
import type { FansLeaderboardRow } from '../types'
import { FanRow, FanRowHeader } from './FanRow'

/** Ranks shown above and below the viewer after a jump to their position. */
const SELF_RANGE_RADIUS = 10

type FansBoardProps = {
  /** Current fans of one club; omit for the global board. */
  clubId?: string
  clubName?: string
  sportType?: string
  emptyState: ReactNode
}

export function FansBoard({
  clubId,
  clubName,
  sportType,
  emptyState
}: FansBoardProps) {
  const t = useTranslations('leaderboardBoard')
  const { data: userMe } = useUserMe()
  const { isClubUser } = useClubMe()
  const myUserId = isClubUser ? undefined : userMe?.user?.id
  const variant = clubId ? 'club' : 'global'

  const { data: position } = useFansLeaderboardPosition(
    sportType,
    clubId,
    Boolean(myUserId)
  )
  const selfRank = position?.rank

  // BAT-860: the club's sport comes in the page's language, so the language
  // is part of the request and of the cache key.
  const locale = useLocale()
  const fetchPage = useCallback(
    (page: number, pageSize: number) =>
      fetchFansLeaderboardPage({ clubId, sportType }, page, pageSize, locale),
    [clubId, sportType, locale]
  )
  const isSelfRow = useCallback(
    (row: FansLeaderboardRow) => row.userId === myUserId,
    [myUserId]
  )

  const board = usePagedLeaderboard({
    queryKey: [
      'fans_leaderboard_page',
      clubId ?? 'global',
      sportType ?? 'all',
      locale
    ],
    fetchPage,
    selfRank,
    isSelfRow: myUserId ? isSelfRow : undefined
  })

  if (board.isLoading) {
    return <LeaderboardLoading />
  }

  if (board.isError) {
    return <LeaderboardLoadError onRetry={board.retry} />
  }

  if (board.blocks.length === 0) {
    return emptyState
  }

  const self = board.selfRow?.row

  return (
    <LeaderboardBoard
      header={<FanRowHeader variant={variant} />}
      blocks={board.blocks}
      getRowKey={row => row.userId}
      renderRow={(row, state) => (
        <FanRow
          row={row}
          variant={variant}
          isSelf={state.isSelf}
          highlighted={state.highlighted}
        />
      )}
      isSelfRow={myUserId ? isSelfRow : undefined}
      selfRow={board.selfRow}
      selfLabel={
        clubName ? t('yourPositionIn', { club: clubName }) : t('yourPosition')
      }
      pill={
        self
          ? {
              visual: <FanAvatar src={self.avatarUrl} alt="" compact />,
              label: t('myRank.label'),
              rank: self.rank,
              value: formatLeaderboardNumber(self.totalViews),
              unit: t('myRank.views')
            }
          : undefined
      }
      onRevealSelf={
        selfRank
          ? () => board.revealRank(selfRank, SELF_RANGE_RADIUS)
          : undefined
      }
      hasMore={board.hasMore}
      isFetchingMore={board.isFetchingMore}
      onLoadMore={board.loadMore}
      onLoadPrevious={board.loadPrevious}
      labels={{
        loadMore: t('loadMore'),
        showHigher: t('showHigher'),
        jump: t('myRank.jump')
      }}
    />
  )
}
